/* eslint-disable unicode-bom */
import React, { useState, useEffect, createElement } from 'react';
import { FiX, FiUser, FiLock, FiImage, FiSave, FiUsers, FiTrash2, FiEdit2, FiPlus } from 'react-icons/fi';

interface SettingsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: any;
  onUserUpdate: (updatedUser: any) => void;
  initialTab?: 'profile' | 'password' | 'users';
}

const SettingsSidebar: React.FC<SettingsSidebarProps> = ({ isOpen, onClose, currentUser, onUserUpdate, initialTab = 'profile' }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    profileImage: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: ''
  });
  const [managedUsers, setManagedUsers] = useState<any[]>([]);
  const [userLoading, setUserLoading] = useState(false);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [userForm, setUserForm] = useState({
    name: '',
    username: '',
    role: 'standard',
    password: ''
  });
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const isBishal = currentUser?.username === 'bishal';

  useEffect(() => {
    if (currentUser) {
      setFormData({
        name: currentUser.name || '',
        username: currentUser.username || '',
        profileImage: currentUser.profileImage || ''
      });
      setImagePreview(currentUser.profileImage || null);
    }
  }, [currentUser]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      setMessage({ type: '', text: '' });
    }
  }, [isOpen, initialTab]);

  useEffect(() => {
    if (isOpen && activeTab === 'users' && isBishal) {
      fetchUsers();
    }
  }, [isOpen, activeTab, isBishal]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'Image size must be less than 2MB' });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
        setFormData({ ...formData, profileImage: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProfileUpdate = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('http://localhost:5000/api/auth/update-profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name,
          username: formData.username,
          profileImage: formData.profileImage
        })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('adminUser', JSON.stringify(data.user));
        onUserUpdate(data.user);
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to update profile' });
      }
    } catch (error) {
      console.error('Profile update error:', error);
      setMessage({ type: 'error', text: 'Error updating profile' });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    // Validate passwords
    if (!passwordData.currentPassword || !passwordData.newPassword) {
      setMessage({ type: 'error', text: 'All password fields are required' });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'New password must be at least 8 characters' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('http://localhost:5000/api/auth/change-password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Password changed successfully!' });
        setPasswordData({ currentPassword: '', newPassword: '' });
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to change password' });
      }
    } catch (error) {
      console.error('Password change error:', error);
      setMessage({ type: 'error', text: 'Error changing password' });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setUserLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('http://localhost:5000/api/auth/users', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        setManagedUsers(data.users || []);
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to load users' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error loading users' });
    } finally {
      setUserLoading(false);
    }
  };

  const resetUserForm = () => {
    setEditingUserId(null);
    setUserForm({ name: '', username: '', role: 'standard', password: '' });
  };

  const handleCreateOrUpdateUser = async () => {
    if (!userForm.name || !userForm.username) {
      setMessage({ type: 'error', text: 'Name and username are required' });
      return;
    }

    if (!editingUserId && userForm.password.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }

    if (editingUserId && userForm.password && userForm.password.length < 8) {
      setMessage({ type: 'error', text: 'New password must be at least 8 characters' });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const url = editingUserId
        ? `http://localhost:5000/api/auth/users/${editingUserId}`
        : 'http://localhost:5000/api/auth/users';
      const method = editingUserId ? 'PUT' : 'POST';

      const payload: any = {
        name: userForm.name,
        username: userForm.username,
        role: userForm.role
      };
      if (userForm.password) payload.password = userForm.password;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: editingUserId ? 'User updated successfully' : 'User created successfully' });
        resetUserForm();
        fetchUsers();
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to save user' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Error saving user' });
    } finally {
      setLoading(false);
    }
  };

  const startEditUser = (user: any) => {
    setEditingUserId(user.id);
    setUserForm({
      name: user.name || '',
      username: user.username || '',
      role: user.role || 'standard',
      password: ''
    });
  };

  const handleDeleteUser = async (user: any) => {
    if (!window.confirm(`Delete user "${user.username}"?`)) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`http://localhost:5000/api/auth/users/${user.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: 'User deleted successfully' });
        fetchUsers();
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to delete user' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Error deleting user' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          animation: 'fadeIn 0.2s ease'
        }}
      />

      {/* Sidebar */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '420px',
          height: '100vh',
          background: '#FFFFFF',
          boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.1)',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideInRight 0.3s ease'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: '700',
            color: '#111827'
          }}>
            Settings
          </h2>
          <button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              border: 'none',
              background: '#F3F4F6',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#E5E7EB'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#F3F4F6'}
          >
            {createElement(FiX as any, { size: 20, color: '#6B7280' })}
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          padding: '0 24px',
          borderBottom: '1px solid #E5E7EB',
          overflowX: 'auto'
        }}>
          {[
            { id: 'profile', label: 'Profile', icon: FiUser },
            { id: 'password', label: 'Password', icon: FiLock },
            ...(isBishal ? [{ id: 'users', label: 'Users', icon: FiUsers }] : [])
          ].map((tab: any) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setMessage({ type: '', text: '' });
              }}
              style={{
                padding: '14px 20px',
                border: 'none',
                background: 'transparent',
                borderBottom: activeTab === tab.id ? '2px solid #03A84A' : '2px solid transparent',
                color: activeTab === tab.id ? '#03A84A' : '#6B7280',
                fontWeight: activeTab === tab.id ? '600' : '500',
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease'
              }}
            >
              {createElement(tab.icon as any, { size: 18 })}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px'
        }}>
          {/* Message */}
          {message.text && (
            <div style={{
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '20px',
              background: message.type === 'success' ? '#C7F9E5' : '#FEE2E2',
              border: `1px solid ${message.type === 'success' ? '#03A84A' : '#EF4444'}`,
              color: message.type === 'success' ? '#016C33' : '#991B1B',
              fontSize: '13px',
              fontWeight: '500'
            }}>
              {message.text}
            </div>
          )}

          {activeTab === 'profile' && (
            <div>
              {/* Profile Image */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Profile Picture
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: '2px solid #E5E7EB'
                  }}>
                    <img
                      src={imagePreview || (currentUser?.profileImage?.startsWith('/uploads/') 
                        ? `http://localhost:5000${currentUser.profileImage}` 
                        : `https://ui-avatars.com/api/?name=${encodeURIComponent(formData.name || 'User')}&background=random&color=fff&size=128`)}
                      alt="Profile"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </div>
                  <label style={{
                    padding: '10px 16px',
                    background: '#F3F4F6',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#374151',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#E5E7EB'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#F3F4F6'}
                  >
                    {createElement(FiImage as any, { size: 16 })}
                    {imagePreview ? 'Change Image' : 'Upload Image'}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
                <p style={{
                  marginTop: '8px',
                  fontSize: '12px',
                  color: '#6B7280'
                }}>
                  Max size: 2MB. Image will be resized to 128x128px.
                </p>
              </div>

              {/* Name */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>

              {/* Username */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Username
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>

              {/* Save Button */}
              <button
                onClick={handleProfileUpdate}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: loading ? '#9CA3AF' : '#03A84A',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!loading) e.currentTarget.style.background = '#028A3F';
                }}
                onMouseLeave={(e) => {
                  if (!loading) e.currentTarget.style.background = '#03A84A';
                }}
              >
                {createElement(FiSave as any, { size: 18 })}
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}

          {activeTab === 'password' && (
            <div>
              {/* Current Password */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Current Password
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  placeholder="Enter current password"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>

              {/* New Password */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  New Password
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  placeholder="Enter new password (min 8 characters)"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>

              {/* Change Password Button */}
              <button
                onClick={handlePasswordChange}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: loading ? '#9CA3AF' : '#03A84A',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!loading) e.currentTarget.style.background = '#028A3F';
                }}
                onMouseLeave={(e) => {
                  if (!loading) e.currentTarget.style.background = '#03A84A';
                }}
              >
                {createElement(FiLock as any, { size: 18 })}
                {loading ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          )}

          {activeTab === 'users' && isBishal && (
            <div>
              <h3 style={{ margin: '0 0 12px', color: '#111827', fontSize: '15px', fontWeight: '700' }}>
                Manage Admin & Staff Users
              </h3>

              <div style={{
                padding: '12px',
                border: '1px solid #E5E7EB',
                borderRadius: '10px',
                marginBottom: '14px',
                background: '#FAFAFA'
              }}>
                <div style={{ display: 'grid', gap: '10px' }}>
                  <input
                    type="text"
                    placeholder="Full name"
                    value={userForm.name}
                    onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                    style={{ width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '13px' }}
                  />
                  <input
                    type="text"
                    placeholder="Username"
                    value={userForm.username}
                    onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                    style={{ width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '13px' }}
                  />
                  <select
                    value={userForm.role}
                    onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                    style={{ width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '13px', background: '#fff' }}
                  >
                    <option value="standard">Standard</option>
                    <option value="admin">Admin</option>
                  </select>
                  <input
                    type="password"
                    placeholder={editingUserId ? 'New password (optional, min 8 chars)' : 'Password (min 8 chars)'}
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    style={{ width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '13px' }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                  <button
                    onClick={handleCreateOrUpdateUser}
                    disabled={loading}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      border: 'none',
                      borderRadius: '8px',
                      background: '#03A84A',
                      color: '#fff',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    {createElement(editingUserId ? FiSave as any : FiPlus as any, { size: 16 })}
                    {editingUserId ? 'Update User' : 'Create User'}
                  </button>
                  {editingUserId && (
                    <button
                      onClick={resetUserForm}
                      style={{
                        padding: '10px 12px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        background: '#fff',
                        color: '#374151',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gap: '8px' }}>
                {userLoading ? (
                  <p style={{ color: '#6B7280', fontSize: '13px', margin: 0 }}>Loading users...</p>
                ) : managedUsers.length === 0 ? (
                  <p style={{ color: '#6B7280', fontSize: '13px', margin: 0 }}>No users found.</p>
                ) : (
                  managedUsers.map((user) => (
                    <div
                      key={user.id}
                      style={{
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        padding: '10px 12px',
                        background: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <div>
                        <div style={{ color: '#111827', fontSize: '13px', fontWeight: '700' }}>{user.name}</div>
                        <div style={{ color: '#6B7280', fontSize: '12px' }}>
                          @{user.username} · {user.role}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => startEditUser(user)}
                          style={{ border: 'none', background: '#EEF2FF', color: '#4338CA', borderRadius: '6px', padding: '6px 8px', cursor: 'pointer' }}
                        >
                          {createElement(FiEdit2 as any, { size: 14 })}
                        </button>
                        {user.username !== 'bishal' && (
                          <button
                            onClick={() => handleDeleteUser(user)}
                            style={{ border: 'none', background: '#FEF2F2', color: '#DC2626', borderRadius: '6px', padding: '6px 8px', cursor: 'pointer' }}
                          >
                            {createElement(FiTrash2 as any, { size: 14 })}
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
      `}</style>
    </>
  );
};

export default SettingsSidebar;
